'use client';

import React from 'react';
import Link from 'next/link';
import SmallLogo from '../small_logo';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { Box } from '@mui/material';
import { DiscoBallWithConsistentLoading } from '../disco_ball';

const TikTokIcon = ({ color = "currentColor" }) => {
    return (
        <svg
            fill={color}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 50 50"
            width="24px"
            height="24px"
        >
            <path d="M41,4H9C6.243,4,4,6.243,4,9v32c0,2.757,2.243,5,5,5h32c2.757,0,5-2.243,5-5V9C46,6.243,43.757,4,41,4z M37.006,22.323 c-0.227,0.021-0.457,0.035-0.69,0.035c-2.623,0-4.928-1.349-6.269-3.388c0,5.349,0,11.435,0,11.537c0,4.709-3.818,8.527-8.527,8.527 s-8.527-3.818-8.527-8.527s3.818-8.527,8.527-8.527c0.178,0,0.352,0.016,0.527,0.027v4.202c-0.175-0.021-0.347-0.053-0.527-0.053 c-2.404,0-4.352,1.948-4.352,4.352s1.948,4.352,4.352,4.352s4.527-1.894,4.527-4.298c0-0.095,0.042-19.594,0.042-19.594h4.016 c0.378,3.591,3.277,6.425,6.901,6.685V22.323z" />
        </svg>
    );
};

const Footer = () => {
    return (
        <footer className="bg-[#1C1C34] text-white py-8">
            <div className="container mx-auto px-4 md:px-6">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 pb-8 border-b border-gray-700">
                    {/* Logo and Brand */}
                    <div>
                        <div className="flex items-center mb-4">
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', position: 'relative', ml: -2 }}>
                                <Box sx={{ position: 'absolute', width: '60px', height: '60px', left: 0, top: '50%', transform: 'translateY(-50%)' }}>
                                    <DiscoBallWithConsistentLoading quality="low" size="small" />
                                </Box>
                            </Box>
                        </div>
                    </div>

                    {/* Discover Links */}
                    <div>
                        <h3 className="text-sm font-bold mb-3 uppercase">Discover</h3>
                        <ul className="space-y-2 text-sm">
                            <li>
                                <Link href="/search" className="text-gray-400 hover:text-white transition-colors">
                                    Search Venues
                                </Link>
                            </li>
                            <li>
                                <Link href="/events" className="text-gray-400 hover:text-white transition-colors">
                                    Viewed Events
                                </Link>
                            </li>
                            <li>
                                <Link href="/blog" className="text-gray-400 hover:text-white transition-colors">
                                    Blog
                                </Link>
                            </li>
                            <li>
                                <Link href="/list_your_venue" className="text-gray-400 hover:text-white transition-colors">
                                    List Your Venue
                                </Link>
                            </li>
                            <li>
                                <Link href="/about" className="text-gray-400 hover:text-white transition-colors">
                                    About Us
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Account Links */}
                    <div>
                        <h3 className="text-sm font-bold mb-3 uppercase">Account</h3>
                        <ul className="space-y-2 text-sm">
                            <li>
                                <Link href="/login" className="text-gray-400 hover:text-white transition-colors">
                                    Login
                                </Link>
                            </li>
                            <li>
                                <Link href="/profile" className="text-gray-400 hover:text-white transition-colors">
                                    Profile
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Support Links */}
                    <div>
                        <h3 className="text-sm font-bold mb-3 uppercase">Support</h3>
                        <ul className="space-y-2 text-sm">
                            <li>
                                <Link href="/contact" className="text-gray-400 hover:text-white transition-colors">
                                    Contact Us
                                </Link>
                            </li>
                            <li>
                                <Link href="/terms" className="text-gray-400 hover:text-white transition-colors">
                                    Terms of Service
                                </Link>
                            </li>
                            <li>
                                <Link href="/privacy" className="text-gray-400 hover:text-white transition-colors">
                                    Privacy Policy
                                </Link>
                            </li>
                            <li>
                                <Link href="/venue_terms" className="text-gray-400 hover:text-white transition-colors">
                                    Venue Terms
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Copyright and Social Icons */}
                <div className="flex flex-col md:flex-row justify-between items-center pt-6">
                    <p className="text-gray-400 text-xs mb-4 md:mb-0">
                        © {new Date().getFullYear()} Get Litty, LLC. All rights reserved.
                    </p>

                    {/* Social Icons */}
                    <div className="flex space-x-4">
                        <a href="https://instagram.com/getlittyny" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                            <div className="w-6 h-6 text-gray-400 hover:text-white">
                                <InstagramIcon fontSize="small" />
                            </div>
                        </a>
                        <a href="https://twitter.com/getlittynyc" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                            <div className="w-6 h-6 text-gray-400 hover:text-white">
                                <TwitterIcon fontSize="small" />
                            </div>
                        </a>
                        <a href="https://tiktok.com/@getlittynyc" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                            <div className="w-6 h-6 text-gray-400 hover:text-white">
                                <TikTokIcon />
                            </div>
                        </a>
                        <a href="https://linkedin.com/company/getlitty" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                            <div className="w-6 h-6 text-gray-400 hover:text-white">
                                <LinkedInIcon fontSize="small" />
                            </div>
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer; 